import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, apiFetch } from '../api/client';
import type {
  AuthMeResponse,
  CompactFeatureSnapshot,
  DeploymentMode,
  Permission,
  UserRole,
} from '@grid2/shared-types';

/**
 * Session/role context (ADR-0004 P5.0 / F6).
 *
 * Fetches the extended `GET /api/auth/me` once on mount and exposes the
 * caller's identity, tenant roles, and effective-permission summary via
 * `useSession()`. This is the ONLY sanctioned place the web reads roles —
 * every gating decision in P5 (and beyond) goes through `hasPermission` /
 * `isSystemAdmin` / `isAppAdmin`, never an ad-hoc role string comparison.
 *
 * Explanatory only (ADR-0004 E1): every mutating/execution endpoint still
 * re-checks server-side. Hiding or disabling a control here is a UX nicety,
 * not the enforcement boundary.
 *
 * Scope: `SessionProvider` wraps only the authenticated app shell (see
 * `App.tsx`, the `AppLayout` route element) — NOT the public form respondent
 * routes (`/forms/public/:formId`, `/forms/:formId/view`), which render
 * outside `AppLayout` and must never trigger this fetch or be blocked by its
 * loading state (ADR-0004 E4 public-respondent invariant).
 */
export interface SessionContextValue {
  userId: string;
  orgId: string;
  region: string;
  deploymentMode?: DeploymentMode;
  entitlements?: string[];
  /**
   * The current session's org identity (ADR-0010 Phase 2) — mirrors
   * `AuthMeResponse.org`. Consumed by the nav `OrgSwitcher` and the
   * post-login `OrgSelectionScreen` picker to display the CURRENT org
   * alongside the `GET /api/orgs` list of switch targets.
   */
  org: { orgId: string; name: string; slug: string };
  /** The caller's tenant-wide roles (ADR-0004 D1 axis 1): 'owner' (System Admin), 'admin' (App Admin), 'user', 'billing-admin'. */
  tenantRoles: UserRole[];
  /** The union of DEFAULT_ROLE_PERMISSIONS over tenantRoles — the effective permission summary the web gates UI on. */
  permissions: Permission[];
  /**
   * Compact, server-resolved feature policy for explanatory UI gating.
   * Server middleware remains authoritative for every protected operation.
   */
  features: CompactFeatureSnapshot;
  /** True if the caller holds a given permission string (case-sensitive, exact match). */
  hasPermission: (permission: Permission) => boolean;
  /** True if `tenantRoles` includes 'owner' (ADR-0004 D1: System Admin — tenant-role assignment, billing, everything). */
  isSystemAdmin: boolean;
  /**
   * True if `tenantRoles` includes 'admin' OR 'owner' — App Admin reach is a
   * strict subset of System Admin reach (D1: System Admin has "Everything"),
   * so a System Admin should never be excluded from an App-Admin-gated
   * control by this flag alone.
   */
  isAppAdmin: boolean;
  /** True while the initial `/api/auth/me` fetch is in flight. */
  isLoading: boolean;
  /** Last fetch error; transient background failures retain the last known session. */
  error: string | null;
  /**
   * Updates the current org identity after a confirmed in-app rename. This
   * keeps persistent app-shell consumers (for example the sidebar org label)
   * synchronized without making their correctness depend on a second request.
   */
  updateCurrentOrg: (org: AuthMeResponse['org']) => void;
  /** Re-fetches `/api/auth/me` (e.g. after a role change elsewhere takes effect). */
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

function emptySession(): AuthMeResponse {
  return {
    userId: '',
    orgId: '',
    region: '',
    tenantRoles: [],
    permissions: [],
    org: { orgId: '', name: '', slug: '' },
    features: {
      catalogVersion: '0.0',
      policyVersion: 0,
      authorityEpoch: 0,
      enabled: [],
      upgrade: [],
      hidden: [],
      blocked: {},
      unavailable: true,
    },
  };
}

export interface SessionProviderProps {
  children: ReactNode;
}

export function SessionProvider({ children }: SessionProviderProps) {
  const [me, setMe] = useState<AuthMeResponse>(emptySession);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (background = false) => {
    if (!background) setIsLoading(true);
    setError(null);
    try {
      const response = await apiFetch<AuthMeResponse>('/api/auth/me');
      setMe(response);
    } catch (err) {
      // A transient poll failure must not discard organization-scoped drafts.
      // A real session expiry still clears identity and permissions immediately.
      if (!background || (err instanceof ApiError && err.status === 401)) {
        setMe(emptySession());
      }
      setError(err instanceof Error ? err.message : 'Failed to load session');
    } finally {
      if (!background) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(false);
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load(true);
    }, 30_000);
    return () => window.clearInterval(interval);
  }, [load]);

  const updateCurrentOrg = useCallback((org: AuthMeResponse['org']) => {
    setMe((current) => {
      if (current.orgId !== org.orgId) return current;
      return { ...current, org };
    });
  }, []);

  const value = useMemo<SessionContextValue>(() => {
    const tenantRoles = me.tenantRoles;
    const permissionSet = new Set(me.permissions);
    return {
      userId: me.userId,
      orgId: me.orgId,
      region: me.region,
      deploymentMode: me.deploymentMode,
      entitlements: me.entitlements,
      org: me.org,
      tenantRoles,
      permissions: me.permissions,
      features: me.features ?? emptySession().features!,
      hasPermission: (permission: Permission) => permissionSet.has(permission),
      isSystemAdmin: tenantRoles.includes('owner'),
      isAppAdmin: tenantRoles.includes('admin') || tenantRoles.includes('owner'),
      isLoading,
      error,
      updateCurrentOrg,
      refresh: () => load(false),
    };
  }, [me, isLoading, error, updateCurrentOrg, load]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/**
 * Reads the current session/role context. Must be called from a component
 * rendered under `SessionProvider` (the authenticated app shell) — throws
 * otherwise so a misplaced consumer fails fast in development rather than
 * silently gating on an empty/anonymous session.
 */
export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error('useSession() must be called within a <SessionProvider> (the authenticated app shell).');
  }
  return ctx;
}
